import { motion } from "framer-motion";
import { AlertTriangle, Info } from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { Reveal } from "@/components/Reveal";
import { LEGAL_UPDATED, LEGAL_IS_DRAFT } from "@/lib/legal";

/**
 * Rahmen für Rechtstexte. Bewusst ruhiger als die Marketingseiten: schmale
 * Textspalte, keine Glows, keine Bilder - hier zählt Lesbarkeit. `notice`
 * ist ein optionaler Hinweis unter dem Kopf: { title, text }.
 */
export const LegalPage = ({ eyebrow, title, lead, notice, sidebarItems, children }) => (
  <PageLayout sidebarItems={sidebarItems} title={title} testId="legal-main">
    <section
      id="top"
      data-testid="legal-header"
      className="relative pt-36 pb-16 lg:pt-44 lg:pb-20"
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
        <div className="absolute -top-24 -left-24 w-[460px] h-[460px] rounded-full bg-brand-orange/10 blur-[150px]" />
      </div>

      <div className="px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl"
        >
          <p className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-brand-orange mb-6">
            {eyebrow}
          </p>
          <h1 className="font-display font-black tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-[0.98]">
            {title}
          </h1>
          {lead && (
            <p className="mt-7 text-base md:text-lg leading-relaxed text-white/55">
              {lead}
            </p>
          )}
          <p className="mt-7 text-xs uppercase tracking-[0.15em] text-white/35">
            Stand: {LEGAL_UPDATED}
          </p>

          {notice && (
            <div
              data-testid="legal-notice"
              className="mt-10 flex gap-4 rounded-2xl border border-white/15 px-6 py-5"
            >
              <Info className="w-5 h-5 shrink-0 text-brand-orange mt-0.5" />
              <p className="text-sm leading-relaxed text-white/70">
                <strong className="font-semibold text-white">{notice.title}</strong>{" "}
                {notice.text}
              </p>
            </div>
          )}

          {LEGAL_IS_DRAFT && (
            <div className="mt-10 flex gap-4 rounded-2xl border border-brand-orange/40 px-6 py-5">
              <AlertTriangle className="w-5 h-5 shrink-0 text-brand-orange mt-0.5" />
              <p className="text-sm leading-relaxed text-white/70">
                <strong className="font-semibold text-white">
                  Entwurf, noch nicht rechtlich geprüft.
                </strong>{" "}
                Dieser Text bildet den Aufbau ab und enthält Platzhalter, die
                orange markiert sind. Vor dem Livegang müssen alle Angaben
                ergänzt und der gesamte Text anwaltlich geprüft werden.
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </section>

    <div className="px-6 md:px-10 lg:px-16 pb-24 lg:pb-32">
      <div className="max-w-3xl">{children}</div>
    </div>
  </PageLayout>
);

/** Ein nummerierter Abschnitt innerhalb eines Rechtstextes. */
export const LegalSection = ({ id, title, children }) => (
  <section
    id={id}
    data-testid={`legal-section-${id}`}
    className="scroll-mt-32 border-t border-white/10 pt-12 mt-12 first:border-t-0 first:pt-0 first:mt-0"
  >
    <Reveal>
      <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-6 hyphens-auto break-words">
        {title}
      </h2>
      <div className="space-y-4 text-base leading-relaxed text-white/60">
        {children}
      </div>
    </Reveal>
  </section>
);

/** Unterüberschrift innerhalb eines Abschnitts. */
export const LegalHeading = ({ children }) => (
  <h3 className="font-display font-bold text-lg text-white pt-4">{children}</h3>
);

/** Aufzählung im Fließtext-Stil. */
export const LegalList = ({ items }) => (
  <ul className="space-y-2.5 pl-1">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-3">
        <span className="mt-2.5 w-1.5 h-1.5 shrink-0 rounded-full bg-white/30" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

/**
 * Markiert eine noch offene Angabe. Absichtlich auffällig, damit vor dem
 * Livegang nichts übersehen wird.
 */
export const Placeholder = ({ children }) => (
  <span className="rounded border border-dashed border-brand-orange/60 px-1.5 py-0.5 text-brand-orange">
    {children}
  </span>
);

/** Tabelle für Rechtstexte, scrollt auf schmalen Geräten waagerecht. */
export const LegalTable = ({ head, rows }) => (
  <div className="overflow-x-auto rounded-2xl border border-white/10">
    <table className="w-full min-w-[520px] text-left text-sm">
      <thead>
        <tr className="border-b border-white/10">
          {head.map((h) => (
            <th
              key={h}
              className="px-5 py-3.5 text-[11px] uppercase tracking-[0.15em] font-bold text-white/45"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-white/[0.06] last:border-b-0">
            {row.map((cell, j) => (
              <td
                key={j}
                className={`px-5 py-4 align-top ${
                  j === 0 ? "text-white/85 font-medium" : "text-white/55"
                }`}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * Markiert einen Formulierungsvorschlag, der noch bestätigt werden muss.
 * Anders als `Placeholder` fehlt hier keine Angabe - der Text ist nur noch
 * nicht entschieden.
 */
export const Suggestion = ({ children }) => (
  <span className="rounded border border-dashed border-brand-blue/60 px-1.5 py-0.5 text-brand-blue">
    {children}
  </span>
);
